<template>
  <BaseModal
    useForm
    :heading="t('register.payment_methods_modal.heading')"
    small
    testId="edit-payment-methods"
    @submit="handleSubmit"
    @close="emit('close')"
  >
    <Flex direction="column">
      <div :class="$style.container">
        <BaseCheckboxCard
          v-for="method in methods"
          :key="method.key"
          :selected="method.checked"
          :label="t(`global.payments.${method.key}`, brandNamePay())"
          @update:selected="method.checked = $event"
          v-test="`register-payment-method-${method.key}`"
        />
      </div>
      <BaseText
        v-if="v$.checkedPaymentMethods.$errors.length"
        color="error"
        size="s"
        v-test="'register-payment-methods-error'"
      >
        * {{ t('global.validations.required') }}
      </BaseText>
    </Flex>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="emit('close')"
        v-test="'register-payment-methods-cancel'"
      >
        {{ t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :loading="saving"
        v-test="'register-payment-methods-save'"
      >
        {{ t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import useVuelidate from '@vuelidate/core';
import * as validators from '@vuelidate/validators';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandNamePay } from '@/helpers/brand-name';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';

const AVAILABLE_PAYMENT_METHODS = [
  'pin',
  'cash',
  'creditcard',
  'bank',
  'cheque',
  'coupon',
  'giftcard',
  'prepaid_card',
];

const NON_TOGGLEABLE_PAYMENT_METHODS = ['pos'];

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();
const { company, updateCompany } = useCompanyStore();

const allowedPaymentMethods =
  company.settings?.sales?.allowedPaymentMethods || [];

const methods = ref(
  AVAILABLE_PAYMENT_METHODS.map((key) => ({
    key,
    checked:
      !allowedPaymentMethods.length || allowedPaymentMethods.includes(key),
  })),
);

const saving = ref(false);

const checkedPaymentMethods = computed(() =>
  methods.value.filter((method) => method.checked).map((method) => method.key),
);

const v$ = useVuelidate(
  { checkedPaymentMethods: { required: validators.required } },
  { checkedPaymentMethods },
);

const handleSubmit = () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  const selectedPaymentMethods = [
    ...checkedPaymentMethods.value,
    ...NON_TOGGLEABLE_PAYMENT_METHODS,
  ];

  saving.value = true;
  updateCompany({
    settings: { sales: { allowedPaymentMethods: selectedPaymentMethods } },
  })
    .then(() => {
      flash(t('global.flash.settings_updated'));
      emit('submit', selectedPaymentMethods);
      emit('close');
    })
    .finally(() => {
      saving.value = false;
    });
};
</script>

<style
  lang="scss"
  module
>

.container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacing;
}
</style>
